// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {get, isEmpty, isPlainObject, noop} from 'lodash';
import classnames from 'classnames';

// Components
import Error from './error';
import FormUpdated from './form-updated';
import Loading from './loading';

// Redux Actions
import * as formDataActions from '../actions/form-data-actions';
import {refreshServicesData} from '../actions/services-data-actions';

class Form extends Component {
    state = {
        successfullySubmitted: false,
    };

    getChildContext() {
        const {setFieldErrors, unregisterFieldErrors, unregisterFieldValue} = this.props;

        return {
            formName: this.formName,
            isFormValid: this.isFormValid,
            onSubmit: this.handleSubmit,
            onReset: this.handleReset,
            setFieldErrors,
            setFieldValue: this.setFieldValue,
            unregisterFieldErrors,
            unregisterFieldValue,
        };
    }

    UNSAFE_componentWillMount() {
        const {name, registerForm} = this.props;
        this.formName = name || this.context.name;

        if (!this.isFormRegistered()) {
            registerForm(this.formName).then(() => {
                this.setFormData();
            });
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const {name, submitData} = nextProps;
        const prevFormData = this.getFormData(this.props);

        this.setFormData(nextProps);

        if (name && this.formName !== name) {
            this.formName = name;
        }

        if (
            (submitData,
            prevFormData.fetching && !this.formData.fetching && this.formData.fetched && this.isFormValid())
        ) {
            this.setState({successfullySubmitted: true});
        }
    }

    setFormData(props = this.props) {
        this.formData = this.getFormData(props);
    }

    getFormData(props) {
        return get(props.formData, this.formName, {});
    }

    getClass() {
        return classnames('form', this.props.className);
    }

    handleFormSubmit = event => {
        event.preventDefault();
    };

    handleSubmit = event => {
        if (event && event.preventDefault) {
            event.preventDefault();
        }

        const {onSubmit, formSubmitFetch, submitData} = this.props;

        if (this.isFormValid()) {
            onSubmit(event);
            if (submitData) {
                formSubmitFetch(this.formName);
            }
        }
    };

    handleReset = event => {
        const {onReset, resetForm, submitData} = this.props;

        if (event) {
            event.preventDefault();
        }

        resetForm(this.formName, !submitData, true).then(() => onReset());
    };

    formClearErrors = () => {
        this.props.unregisterFormErrors(this.formName);
    };

    formSubmitReset = () => {
        const {onReset, formSubmitReset, refreshServicesData, resetOnSubmit, updateServicesDataKey} = this.props;

        onReset(true);
        formSubmitReset(this.formName, resetOnSubmit);
        this.setState({successfullySubmitted: false});

        if (updateServicesDataKey) {
            refreshServicesData(updateServicesDataKey);
        }
    };

    setFieldValue = (fieldName, value, operator) => this.props.setFieldValue(this.formName, fieldName, value, operator);

    isFormRegistered() {
        return isPlainObject(this.props.formData[this.formName]);
    }

    isFormValid = () => !(this.hasFieldErrors() || this.hasFormErrors());

    hasFormErrors() {
        const formErrors = get(this.formData, 'formErrors');

        return formErrors && formErrors.length;
    }

    hasFieldErrors() {
        return !isEmpty(get(this.formData, 'fieldErrors'));
    }

    getErrorProps() {
        return {
            absolutePositioned: this.props.absolutePositionedResponse,
            className: 'form--error',
            error: get(this.formData, 'formErrors'),
            fixedPositioned: this.props.fixedPositionedResponse,
            onClear: this.formClearErrors,
            onReset: this.formSubmitReset,
            resetButtonChildren: 'reset form',
        };
    }

    renderForm() {
        let content = null;
        const {children, absolutePositionedResponse} = this.props;

        if (!this.state.successfullySubmitted) {
            content = (
                <form
                    name={this.formName}
                    style={{border: '1px solid #c8c8c8', boxShadow: '7px 7px 5px 0px #c8c8c8'}}
                    onSubmit={this.handleFormSubmit}
                >
                    {children}
                </form>
            );
        }

        if (this.state.successfullySubmitted) {
            content = (
                <FormUpdated absolutePositioned={absolutePositionedResponse} formSubmitReset={this.formSubmitReset} />
            );
        }

        return content;
    }

    isFetching() {
        return get(this.formData, 'fetching');
    }

    render() {
        return (
            <div
                className={this.getClass()}
                ref={this.props.formRef}
                style={{width: '100%', display: 'flex', justifyContent: 'center'}}
            >
                <Loading loading={this.isFetching()} />
                {this.renderForm()}
                <Error {...this.getErrorProps()} />
            </div>
        );
    }
}

Form.propTypes = {
    absolutePositionedResponse: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
    formRef: PropTypes.func,
    fixedPositionedResponse: PropTypes.bool,
    formData: PropTypes.object.isRequired,
    formSubmitReset: PropTypes.func.isRequired,
    name: PropTypes.string,
    onSubmit: PropTypes.func,
    onReset: PropTypes.func,
    registerForm: PropTypes.func.isRequired,
    refreshServicesData: PropTypes.func,
    resetForm: PropTypes.func.isRequired,
    resetOnSubmit: PropTypes.bool,
    searchFetching: PropTypes.object,
    servicesDataKey: PropTypes.string,
    setFieldErrors: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    setFormSubmitted: PropTypes.func.isRequired,
    submitData: PropTypes.bool,
    formSubmitFetch: PropTypes.func.isRequired,
    unregisterFieldErrors: PropTypes.func.isRequired,
    unregisterFormErrors: PropTypes.func.isRequired,
    unregisterFieldValue: PropTypes.func.isRequired,
    updateServicesDataKey: PropTypes.string,
};

Form.defaultProps = {
    onSubmit: noop,
    onReset: noop,
};

Form.contextTypes = {
    name: PropTypes.string,
};

Form.childContextTypes = {
    formName: PropTypes.string.isRequired,
    isFormValid: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    setFieldErrors: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    submitData: PropTypes.bool,
    unregisterFieldErrors: PropTypes.func.isRequired,
    unregisterFieldValue: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
    return {
        formData: state.formData,
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({...formDataActions, refreshServicesData}, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(Form);
